// src/App.jsx
import React, { useState, useMemo } from "react";
import "./styles.css";
import { Pill, Checklist, Scrapbook } from "./components/CheerUI";

/* -------------------------------------------------------------------------- */
/* 1. IMPORT DATA FILES                                                       */
/* -------------------------------------------------------------------------- */
import { CheerFactor } from "./data/gyms/CheerFactor";
import { MaineStars } from "./data/gyms/MaineStars";
import { CheerSportSharksNE } from "./data/gyms/CheerSportSharksNE";
import { CheerUP } from "./data/gyms/CheerUP";
import { CTVipers } from "./data/gyms/CTVipers";
import { EastCoast } from "./data/gyms/EastCoast";
import { ElementAllStars } from "./data/gyms/ElementAllStars";
import { Elite } from "./data/gyms/Elite";
import { ExtremeForce } from "./data/gyms/ExtremeForce";
import { Lightning } from "./data/gyms/Lightning";
import { AList } from "./data/gyms/AList";
import { Athletix } from "./data/gyms/Athletix";
import { NECheer } from "./data/gyms/NECheer";
import { NorthStar } from "./data/gyms/NorthStar";
import { NorthernLights } from "./data/gyms/NorthernLights";
import { RocketCheer } from "./data/gyms/RocketCheer";
import { TopTier } from "./data/gyms/TopTier";
import { Wicked } from "./data/gyms/Wicked";
import { Woodlands } from "./data/gyms/Woodlands";
import { Legend } from "./data/gyms/Legend";
import { Revolution } from "./data/gyms/Revolution";
import { Rogue } from "./data/gyms/Rogue";
import { StTeresas } from "./data/gyms/StTeresas";

const GYM_REGISTRY = [
  CheerFactor,
  MaineStars,
  CheerSportSharksNE,
  CheerUP,
  CTVipers,
  EastCoast,
  ElementAllStars,
  Elite,
  ExtremeForce,
  Lightning,
  AList,
  Athletix,
  NECheer,
  NorthStar,
  NorthernLights,
  RocketCheer,
  TopTier,
  Wicked,
  Woodlands,
  Legend,
  Revolution,
  Rogue,
  StTeresas,
];

const MY_ATHLETES = {
  emma: {
    name: "Emma",
    color: "#E879F9",
    bg: "#FDF4FF",
    teams: ["glamour", "mirage"],
  },
  laura: {
    name: "Laura",
    color: "#2DD4BF",
    bg: "#F0FDFA",
    teams: ["fantasy", "mirage"],
  },
  hasTeam: (id) => ["glamour", "mirage", "fantasy"].includes(id),
};

/* -------------------------------------------------------------------------- */
/* 2. UTILITIES & LOGO GENERATOR                                              */
/* -------------------------------------------------------------------------- */
const parseTime = (timeStr) => {
  if (!timeStr) return 9999;
  const [time, modifier] = timeStr.split(" ");
  let [hours, minutes] = time.split(":");
  if (hours === "12") hours = "0";
  if (modifier === "PM") hours = parseInt(hours, 10) + 12;
  return parseInt(hours, 10) * 60 + parseInt(minutes, 10);
};

function GymLogo({ name, color, logoUrl, size = 40 }) {
  if (logoUrl) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
          border: "2px solid white",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <img
          src={logoUrl}
          alt={name}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </div>
    );
  }
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("");
  return (
    <div
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, ${color}, #1e293b)`,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        fontWeight: 900,
        fontSize: size * 0.4,
        boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
        border: "2px solid white",
        textShadow: "0 1px 2px rgba(0,0,0,0.3)",
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 3. PERFORMANCE CARD COMPONENT                                              */
/* -------------------------------------------------------------------------- */
function PerformanceCard({ event }) {
  let myAthleteTag = null;
  if (event.gymId === "cheerfactor") {
    const isEmma = MY_ATHLETES.emma.teams.includes(event.teamId);
    const isLaura = MY_ATHLETES.laura.teams.includes(event.teamId);
    if (isEmma && isLaura)
      myAthleteTag = { name: "Emma & Laura", color: "#6366F1", bg: "#EEF2FF" };
    else if (isEmma) myAthleteTag = MY_ATHLETES.emma;
    else if (isLaura) myAthleteTag = MY_ATHLETES.laura;
  }

  return (
    <div
      className="card"
      style={{
        borderLeft: `6px solid ${myAthleteTag ? "#F59E0B" : event.gymColor}`,
        marginBottom: 10,
        padding: "12px 16px",
        position: "relative",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      {event.teamId === "fantasy" && event.gymId === "cheerfactor" && (
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            background: "#F59E0B",
            color: "#fff",
            fontSize: "0.55rem",
            fontWeight: 800,
            padding: "2px 6px",
            borderBottomLeftRadius: 6,
          }}
        >
          TEAM MOM
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Left Side: Logo + Info */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <GymLogo
            name={event.gymName}
            color={event.gymColor}
            logoUrl={event.gymLogoUrl}
            size={42}
          />

          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 2,
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  color: "#0F172A",
                  fontSize: "1rem",
                  lineHeight: 1.1,
                }}
              >
                {event.teamName}
              </div>
              {myAthleteTag && (
                <span
                  style={{
                    background: myAthleteTag.bg,
                    color: myAthleteTag.color,
                    padding: "2px 6px",
                    borderRadius: 99,
                    fontSize: "0.55rem",
                    fontWeight: 800,
                    textTransform: "uppercase",
                  }}
                >
                  {myAthleteTag.name}
                </span>
              )}
            </div>

            <div
              style={{ fontSize: "0.75rem", color: "#64748B", fontWeight: 500 }}
            >
              {event.gymName} · {event.level}
            </div>
          </div>
        </div>

        {/* Right Side: Time */}
        <div style={{ textAlign: "right", minWidth: 70 }}>
          <div
            style={{ fontSize: "1.1rem", fontWeight: 900, color: "#0F172A" }}
          >
            {event.time}
          </div>
          <div
            style={{
              fontSize: "0.6rem",
              color: "#64748B",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
          >
            {event.panel || "Floor"}
          </div>
        </div>
      </div>

      {/* Expanded Logistics (Only for My Gym) */}
      {event.gymId === "cheerfactor" && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: "1px dashed #E2E8F0",
            display: "flex",
            gap: 12,
          }}
        >
          <div style={{ fontSize: "0.7rem", color: "#334155" }}>
            <strong>Warm Up:</strong> {event.meet}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 4. MAIN APP COMPONENT                                                      */
/* -------------------------------------------------------------------------- */
export default function App() {
  const [activeEvent, setActiveEvent] = useState("worcester");
  const [view, setView] = useState("athletes");
  const [day, setDay] = useState("Saturday");
  const [gymFilter, setGymFilter] = useState("all");

  const allEvents = useMemo(() => {
    let events = [];
    GYM_REGISTRY.forEach((gym) => {
      if (gym && gym.schedule) {
        gym.schedule.forEach((ev) => {
          const teamInfo = gym.teams.find((t) => t.id === ev.teamId);
          events.push({
            ...ev,
            gymId: gym.id,
            gymName: gym.name,
            gymColor: gym.color,
            gymLogoUrl: gym.logoUrl,
            teamName: teamInfo ? teamInfo.name : ev.teamId,
            level: teamInfo ? teamInfo.level : "",
            sortTime: parseTime(ev.time),
          });
        });
      }
    });
    return events.sort((a, b) => a.sortTime - b.sortTime);
  }, []);

  const visibleEvents = allEvents.filter((ev) => {
    if (ev.day !== day) return false;
    if (view === "athletes")
      return ev.gymId === "cheerfactor" && MY_ATHLETES.hasTeam(ev.teamId);
    if (view === "program") return ev.gymId === "cheerfactor";
    if (view === "event")
      return gymFilter === "all" ? true : ev.gymId === gymFilter;
    return true;
  });

  return (
    <div className="container">
      {/* HEADER SECTION - Increased padding-bottom to 120px for better spacing */}
      <header
        className="section"
        style={{
          background: "linear-gradient(135deg, #1E1B4B 0%, #312E81 100%)",
          color: "white",
          padding: "32px 24px 120px 24px",
          borderRadius: "0 0 32px 32px",
          marginTop: -20,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "relative", zIndex: 2 }}>
          <div
            style={{
              fontSize: "0.75rem",
              opacity: 0.8,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              marginBottom: 8,
              fontWeight: 700,
            }}
          >
            Season 2026
          </div>
          <h1
            style={{
              fontSize: "2.5rem",
              margin: 0,
              fontWeight: 900,
              letterSpacing: "-1px",
            }}
          >
            CHEERFACTOR
          </h1>
        </div>
      </header>

      {/* FLOATING CARD SECTION - Adjusted margin to -70px */}
      <div style={{ marginTop: -70, padding: "0 16px", position: "relative", zIndex: 10 }}>
        <div
          className="card"
          style={{
            padding: 8,
            border: "none",
            boxShadow: "0 10px 30px -5px rgba(0,0,0,0.15)",
            background: "white",
            borderRadius: 16,
          }}
        >
          {/* TAB NAVIGATION - Added Flex and gap for better spacing */}
          <div 
            className="event-nav" 
            style={{ 
              marginBottom: 16, 
              display: "flex", 
              gap: 8,
              justifyContent: "space-between" 
            }}
          >
            <button
              className={`event-tab ${
                activeEvent === "springfield" ? "active" : ""
              }`}
              onClick={() => setActiveEvent("springfield")}
              style={{
                flex: 1,
                border: "none",
                background: activeEvent === "springfield" ? "#EEF2FF" : "transparent",
                padding: "12px 4px",
                borderRadius: 12,
                cursor: "pointer"
              }}
            >
              <div className="status" style={{ color: "#64748B", fontSize: "0.65rem", fontWeight: 800, marginBottom: 4 }}>
                ✅ PAST
              </div>
              <div className="title" style={{ fontWeight: 700, fontSize: "0.9rem", color: "#1E293B" }}>Springfield</div>
            </button>
            
            <button
              className={`event-tab ${
                activeEvent === "worcester" ? "active" : ""
              }`}
              onClick={() => setActiveEvent("worcester")}
              style={{
                flex: 1,
                border: "none",
                background: activeEvent === "worcester" ? "#EEF2FF" : "transparent",
                padding: "12px 4px",
                borderRadius: 12,
                cursor: "pointer"
              }}
            >
              <div className="status" style={{ color: "#E11D48", fontSize: "0.65rem", fontWeight: 800, marginBottom: 4 }}>
                🔥 NEXT UP
              </div>
              <div className="title" style={{ fontWeight: 700, fontSize: "0.9rem", color: "#1E293B" }}>Worcester</div>
            </button>
            
            <button
              className={`event-tab ${
                activeEvent === "summit" ? "active" : ""
              }`}
              onClick={() => setActiveEvent("summit")}
              style={{
                flex: 1,
                border: "none",
                background: activeEvent === "summit" ? "#EEF2FF" : "transparent",
                padding: "12px 4px",
                borderRadius: 12,
                cursor: "pointer"
              }}
            >
              <div className="status" style={{ color: "#312E81", fontSize: "0.65rem", fontWeight: 800, marginBottom: 4 }}>
                🎯 GOAL
              </div>
              <div className="title" style={{ fontWeight: 700, fontSize: "0.9rem", color: "#1E293B" }}>Summit</div>
            </button>
          </div>

          {activeEvent === "worcester" && (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 8,
                  background: "#F1F5F9",
                  padding: 4,
                  borderRadius: 12,
                  marginBottom: 16,
                }}
              >
                {[
                  { id: "athletes", label: "My Athletes", icon: "🎀" },
                  { id: "program", label: "CheerFactor", icon: "⚡️" },
                  { id: "event", label: "Full Event", icon: "🌎" },
                ].map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setView(v.id)}
                    style={{
                      padding: "10px 4px",
                      borderRadius: 10,
                      border: "none",
                      background: view === v.id ? "#fff" : "transparent",
                      color: view === v.id ? "#0F172A" : "#64748B",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      cursor: "pointer",
                      boxShadow:
                        view === v.id ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span style={{ fontSize: "1.2rem" }}>{v.icon}</span>
                    {v.label}
                  </button>
                ))}
              </div>

              <div style={{ display: "flex", gap: 6 }}>
                {["Saturday", "Sunday"].map((d) => (
                  <button
                    key={d}
                    onClick={() => setDay(d)}
                    style={{
                      flex: 1,
                      padding: "8px",
                      borderRadius: 8,
                      border: "1px solid",
                      borderColor: day === d ? "#312E81" : "#E2E8F0",
                      background: day === d ? "#312E81" : "#fff",
                      color: day === d ? "#fff" : "#64748B",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      cursor: "pointer",
                    }}
                  >
                    {d.substring(0, 3)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ padding: "24px 16px" }}>
        {view === "event" && activeEvent === "worcester" && (
          <div
            style={{
              overflowX: "auto",
              display: "flex",
              gap: 8,
              paddingBottom: 16,
              marginBottom: 8,
            }}
          >
            <button
              onClick={() => setGymFilter("all")}
              style={{
                padding: "6px 12px",
                borderRadius: 99,
                border: "none",
                background: gymFilter === "all" ? "#0F172A" : "#E2E8F0",
                color: gymFilter === "all" ? "white" : "#64748B",
                fontWeight: 700,
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
            >
              All Programs
            </button>
            {GYM_REGISTRY.map((g) => (
              <button
                key={g.id}
                onClick={() => setGymFilter(g.id)}
                style={{
                  padding: "4px 12px 4px 4px",
                  borderRadius: 99,
                  border: "1px solid #E2E8F0",
                  background: gymFilter === g.id ? g.color : "white",
                  color: gymFilter === g.id ? "white" : "#334155",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  opacity: gymFilter === "all" || gymFilter === g.id ? 1 : 0.5,
                }}
              >
                <GymLogo
                  name={g.name}
                  color={g.color}
                  logoUrl={g.logoUrl}
                  size={24}
                />
                {g.name}
              </button>
            ))}
          </div>
        )}

        {activeEvent === "worcester" ? (
          <div className="animate-fade-in">
            <div
              style={{
                marginBottom: 16,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "#64748B",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {day} · {visibleEvents.length} Events
              </div>
            </div>

            <div className="grid">
              {visibleEvents.length > 0 ? (
                visibleEvents.map((event, i) => (
                  <PerformanceCard key={i} event={event} />
                ))
              ) : (
                <div
                  style={{ textAlign: "center", padding: 60, color: "#94A3B8" }}
                >
                  <div style={{ fontSize: "2rem", marginBottom: 12 }}>💤</div>
                  <div>No performances found for this filter.</div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 40, opacity: 0.5 }}>
            <div style={{ fontSize: "3rem", marginBottom: 16 }}>🚧</div>
            <div>
              {activeEvent === "springfield"
                ? "Event Completed"
                : "Coming Soon"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
